<template>
  <button
    :class="classes"
    :data-test-id="dataTestId"
    :disabled="readonly || disabled"
    @click="emitClick"
  >
    <slot></slot>
  </button>
</template>

<script lang="ts">
interface ButtonProps {
  dataTestId?: string;
  variant?: Variant;
  size?: Size;
  fullwidth?: boolean;
  disabled?: boolean;
  readonly?: boolean;
}
</script>

<script lang="ts" setup>
import { computed, toRefs } from "vue";

import type { Variant, Size } from "./types";

const props = withDefaults(defineProps<ButtonProps>(), {
  dataTestId: "",
  variant: "default",
  size: "medium",
  fullwidth: false,
  disabled: false,
  readonly: false,
});

const { dataTestId, variant, size, fullwidth, disabled, readonly } =
  toRefs(props);

const emit = defineEmits<{
  (event: "on-click"): void;
}>();

const emitClick = (): void => {
  emit("on-click");
};

const mainStyles =
  "tw-m-1 tw-items-center tw-rounded tw-font-medium tw-outline-none tw-outline-offset-0 tw-transition-all focus:tw-outline-4";

const widthStyles = fullwidth.value
  ? "tw-flex tw-w-full tw-justify-center"
  : "tw-inline-flex";

const sizes = {
  small: "tw-text-sm tw-py-1 tw-px-2",
  medium: "tw-text-base tw-py-2 tw-px-6",
  large: "tw-text-lg tw-py-3 tw-px-10",
};

const variants = {
  default:
    "tw-text-primary-grey-900 tw-bg-white hover:tw-bg-primary-grey-50/20 active:tw-bg-primary-grey-50/60 focus:tw-outline-primary-grey-50",
  secondary:
    "tw-text-white tw-bg-primary-grey-700 hover:tw-bg-primary-grey-800 active:tw-bg-primary-grey-900 focus:tw-outline-primary-grey-700/30",
  "secondary-outline":
    "tw-text-primary-grey-700 tw-bg-transparent tw-border-0 tw-ring-1 tw-ring-inset tw-ring-primary-grey-700 enabled:hover:tw-bg-primary-grey-800 enabled:hover:tw-ring-primary-grey-800 enabled:hover:tw-text-white enabled:active:tw-bg-primary-grey-800 enabled:active:tw-text-white enabled:focus:tw-outline-primary-grey-700/30",
  "secondary-ghost":
    "tw-text-primary-grey-700 tw-bg-transparent tw-border-0 tw-ring-0 enabled:hover:tw-bg-primary-grey-250 enabled:hover:tw-text-primary-grey-700 enabled:active:tw-bg-primary-grey-800 enabled:active:tw-text-white enabled:focus:tw-outline-primary-grey-700/30 ",
  info: "tw-text-white tw-bg-branding-blue-500 hover:tw-bg-branding-blue-600 active:tw-bg-branding-blue-700 focus:tw-outline-branding-blue-500/30 ",
  success:
    "tw-text-white tw-bg-system-success-500 hover:tw-bg-system-success-600 active:tw-bg-system-success-700 focus:tw-outline-system-success-500/30 ",
  warning:
    "tw-text-white tw-bg-system-warning-500 hover:tw-bg-system-warning-600 active:tw-bg-system-warning-700 focus:tw-outline-system-warning-500/30 ",
  danger:
    "tw-text-white tw-bg-system-error-500 hover:tw-bg-system-error-600 active:tw-bg-system-error-700 focus:tw-outline-system-error-500/30 ",
  "danger-outline":
    "tw-text-system-error-500 tw-bg-transparent tw-border-0 tw-ring-1 tw-ring-inset tw-ring-system-error-500 enabled:hover:tw-bg-system-error-600 enabled:hover:tw-ring-system-error-600 enabled:hover:tw-text-white enabled:active:tw-bg-system-error-600 enabled:active:tw-text-white enabled:focus:tw-outline-system-error-500/30",
  "link-secondary":
    "tw-bg-none tw-text-primary-grey-700 hover:tw-text-primary-grey-800 active:tw-text-primary-grey-900 focus:tw-outline-primary-grey-700/30",
  "i-am-the-danger":
    "tw-bg-none tw-text-system-error-500 hover:tw-text-system-error-600 active:tw-text-system-error-800 focus:tw-outline-system-error-500/30",
};

const disabledStyles = {
  default: "disabled:tw-bg-primary-grey-400/60 disabled:tw-text-white",
  secondary: "disabled:tw-bg-primary-grey-400/60",
  "secondary-outline":
    "disabled:tw-ring-primary-grey-400/60 disabled:tw-text-primary-grey-400/60 disabled:tw-bg-transparent",
  "secondary-ghost": "disabled:tw-text-primary-grey-400/60",
  info: "disabled:tw-bg-primary-grey-400/60",
  success: "disabled:tw-bg-primary-grey-400/60",
  warning: "disabled:tw-bg-primary-grey-400/60",
  danger: "disabled:tw-bg-primary-grey-400/60",
  "danger-outline":
    "disabled:tw-ring-primary-grey-400/60 disabled:tw-text-primary-grey-400/60 disabled:tw-bg-transparent",
  "link-secondary": "disabled:tw-text-primary-grey-400/60",
  "i-am-the-danger": "disabled:tw-text-primary-grey-400/60",
};

const readonlyStyles = {
  default: "disabled:tw-bg-primary-grey-400/40 disabled:tw-text-white",
  secondary: "disabled:tw-text-white disabled:tw-bg-primary-grey-400",
  "secondary-outline":
    "disabled:tw-text-primary-grey-700 disabled:tw-ring-primary-grey-400",
  "secondary-ghost": "disabled:tw-text-primary-grey-700",
  info: "disabled:tw-text-white disabled:tw-bg-branding-blue-200",
  success: "disabled:tw-text-white disabled:tw-bg-system-success-200",
  warning: "disabled:tw-text-white disabled:tw-bg-system-warning-200",
  danger: "disabled:w-text-white disabled:tw-bg-system-error-200 ",
  "danger-outline":
    "disabled:tw-text-system-error-200 disabled:tw-bg-transparent disabled:tw-ring-system-error-200",
  "link-secondary": "disabled:tw-bg-none disabled:tw-text-primary-grey-400",
  "i-am-the-danger": "disabled:tw-bg-none disabled:tw-text-system-error-200",
};

const classes = computed(() => {
  return [
    mainStyles,
    widthStyles,
    sizes[size.value],
    variants[variant.value],
    readonly.value
      ? readonlyStyles[variant.value]
      : disabledStyles[variant.value],
    readonly.value && "disabled:tw-cursor-not-allowed",
    disabled.value && "disabled:tw-cursor-default",
  ];
});
</script>

<style scoped></style>
